import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Search,
  Eye,
  Download,
  ShoppingBag,
  Home,
  School,
  RefreshCw,
  Package2,
  Filter,
} from "lucide-react";
import { LoadingState } from "./components/LoadingState";
import { ColumnDef } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { DataTable } from "@/components/ui/data-table";

import {
  mockInstallments,
  Order,
  StatusType,
} from "@/lib/mock-data/mockFinancialData";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { useToast } from "@/hooks/use-toast";

import type { DateRange } from "react-day-picker";
import { TooltipWrapper } from "@/components/ui/TooltipWrapper";
import { useUser } from "@/contexts/UserContext";
import { ordersApi } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useRegeneratePDF } from "@/hooks/useRegeneratePDF";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import StateBadge from "@/components/ui/stateBadge";
import { StatusOrdersCompleted } from "@/components/shared/StatusOrdersCompleted";
import { getOrderListStatus } from "@/lib/orderStatus";

export const allStatusTypes: StatusType[] = [
  "Criado",
  "Confirmado",
  "Em Rota",
  "Pendente",
  "Processado",
  "Entregue",
  "Cancelado"
];

const MyOrders = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const savedFiltersJSON = sessionStorage.getItem('myOrdersFilters');
    if (savedFiltersJSON) {
      try {
        const savedFilters = JSON.parse(savedFiltersJSON);
        if (savedFilters.searchQuery) {
          setSearchQuery(savedFilters.searchQuery);
        }
        if (savedFilters.statusFilter) {
          setStatusFilter(savedFilters.statusFilter);
        }
        if (savedFilters.dateRange) {
          const from = savedFilters.dateRange.from ? new Date(savedFilters.dateRange.from) : undefined;
          const to = savedFilters.dateRange.to ? new Date(savedFilters.dateRange.to) : undefined;
          setDateRange({ from, to });
        }
      } catch (error) {
        console.error("Erro ao restaurar filtros de Meus Pedidos:", error);
        sessionStorage.removeItem('myOrdersFilters');
      }
    }

  }, []);

  useEffect(() => {
    const filters = {
      dateRange,
      statusFilter,
      searchQuery,
    };

    const hasActiveFilters = dateRange?.from || statusFilter !== 'all' || searchQuery;

    if (hasActiveFilters) {
      sessionStorage.setItem('myOrdersFilters', JSON.stringify(filters));
    } else {
      sessionStorage.removeItem('myOrdersFilters');
    }
  }, [dateRange, statusFilter, searchQuery]);

  const { selectedClient } = useUser();
  const { regeneratePDF } = useRegeneratePDF();

  const { data: orders, isLoading } = useQuery({
    queryKey: ["orders", "all", selectedClient?.codigo_cnx],
    queryFn: async () => {
      const response = await ordersApi.getByClient(selectedClient.codigo_cnx);

      // Pedidos programados só entram aqui depois de processados: nesse momento o pedido real
      // já vem de ordersApi.getByClient.
      return ([...response.data] as unknown as Order[]).sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    },
    enabled: selectedClient !== null,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });

  const validOrders = orders?.filter((order) => order && order.orderNumber && order.clientName);

  const { toast } = useToast();

  const userOrders = useMemo(() => {
    if (isLoading || validOrders === undefined || !selectedClient) {
      return [];
    }

    return (validOrders as Order[])
      .filter((order) => order.clientCode === selectedClient.codigo_cnx)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [selectedClient, validOrders, isLoading]);

  const filteredOrders = useMemo(() => {
    let filtered = [...userOrders];

    if (dateRange?.from) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createdAt);
        const fromDate = dateRange.from!;
        const toDate = dateRange.to || dateRange.from;
        return orderDate >= fromDate && orderDate <= toDate;
      });
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((order) => getOrderListStatus(order) === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter((order) =>
        order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [userOrders, dateRange, statusFilter, searchQuery]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const handleResetFilters = () => {
    setDateRange(undefined);
    setStatusFilter("all");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDownloadPDF = async (orderNumber: string) => {
    setRegeneratingId(orderNumber);
    try {
      const orderItem = validOrders?.find((o: any) => o.id === orderNumber);
      if (!orderItem) {
        toast({
          title: "Erro",
          description: "Pedido não encontrado",
          variant: "destructive",
        });
        return;
      }

      await regeneratePDF(orderItem);
    } finally {
      setRegeneratingId(null);
    }
  };

  const columns: ColumnDef<Order>[] = [
    {
      accessorKey: "orderNumber",
      header: "Número do Pedido",
      cell: ({ row }) => (
        <span className="font-medium" id={`order-num-${row.original.id}`}>{row.original.orderNumber}</span>
      ),
    },
    {
      accessorKey: "orderDate",
      header: "Data do Pedido",
      cell: ({ row }) => formatDate(new Date(row.original.createdAt)),
    },
    {
      accessorKey: "deliveryDate",
      header: "Previsão de Entrega",
      cell: ({ row }) => row.original?.previsionDelivery != null ? formatDate(new Date(row.original?.previsionDelivery)) : '-',
    },
    {
      accessorKey: "cycleCode",
      header: "Tipo",
      cell: ({ row }) => <StatusOrdersCompleted shipmentBlock={row.original.shipmentBlock} orderNumber={row.original.orderNumber} cycleCode={row.original.cycleCode} />
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const raw = row.original.portalB2BOrderStatus?.toLowerCase();
        const isCreated = raw === 'pending' || raw === 'created';
        return <StateBadge status={isCreated ? 'Criado' : row.original.portalB2BOrderStatus || "Criado"} />;
      },
    },
    {
      accessorKey: "totalAmount",
      header: () => <div>Valor Total</div>,
      cell: ({ row }) => {
        const total = row.original.totalAmount + row.original.shippingAmount;
        return (
          <div className="font-medium">
            <span className="sr-only">Valor total: </span>
            {formatCurrency(total)}
          </div>
        )
      },
    },
    {
      id: "actions",
      header: () => <div className="sr-only">Ações</div>,
      cell: ({ row }) => {
        const isConfirmed = row.original.portalB2BOrderStatus.toLowerCase() === 'confirmed';
        return (
          <div className="flex justify-end gap-2">
            <TooltipWrapper content="Ver detalhes do pedido">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Ver detalhes do pedido ${row.original.orderNumber}`}
                onClick={() => {
                  navigate(`/customer-portal/pedidos/${row.original.id}`, {
                    state: { typePedido: row.original?.portalB2BOrderStatus == 'confirmed' ? 'PROGRAMADO' : 'NORMAL' }
                  })
                }}
              >
                <Eye className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipWrapper>
            <TooltipWrapper content="Baixar PDF do pedido">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Baixar PDF do pedido ${row.original.orderNumber}`}
                onClick={() => handleDownloadPDF(row.original.id)}
                disabled={regeneratingId === row.original.id || isConfirmed}
              >
                {regeneratingId === row.original.id ? (
                  <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </TooltipWrapper>
          </div>
        )
      }
    },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, statusFilter, searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <header role="banner">
        <div className="container mx-auto px-4 pt-6">
          <Breadcrumb className="mb-6" aria-label="Navegação estrutural">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/customer-portal" className="flex items-center gap-1">
                    <Home className="h-3 w-3" aria-hidden="true" /> Início
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator aria-hidden="true" />
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-1">
                  <Package2 className="h-3 w-3" aria-hidden="true" /> Pedidos Realizados
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" id="page-title">
                <Package2 className="h-6 w-6 text-blue-600" aria-hidden="true" />
                Pedidos Realizados
              </h1>
              <p className="text-slate-500 mt-1">
                Acompanhe o status e os detalhes dos seus pedidos.
              </p>
            </div>
            {selectedClient && (
              <div className="bg-blue-50 border border-blue-100 px-4 py-2 rounded-lg flex items-center gap-3" role="status" aria-label="Unidade selecionada">
                <div className="bg-blue-100 p-2 rounded-full">
                  <School className="h-4 w-4 text-blue-600" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider leading-none mb-1">Unidade Selecionada</p>
                  <p className="text-sm font-bold text-blue-900 leading-none">{selectedClient.clientFantasyName || selectedClient.nome_cliente}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6" role="main" aria-labelledby="page-title">
        <div className="sr-only" aria-live="polite" role="status">
          {dateRange || statusFilter !== "all" || searchQuery
            ? `${filteredOrders.length} pedidos encontrados para os filtros aplicados.`
            : `Mostrando todos os ${filteredOrders.length} pedidos.`}
        </div>
        <section aria-labelledby="filters-title">
          <Card className={cn("mb-6 border-border shadow-sm", !!(dateRange || statusFilter !== "all" || searchQuery) && "border-primary/50 bg-primary/5")}>
            <CardHeader>
              <CardTitle id="filters-title" className="text-lg font-semibold flex items-center gap-2">
                <Filter className="h-5 w-5" aria-hidden="true" /> Filtros
                {!!(dateRange || statusFilter !== "all" || searchQuery) && <Badge variant="secondary" className="ml-2">Ativos</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 flex-1">
                <div className="space-y-2">
                  <Label htmlFor="search-filter">Busca</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                      id="search-filter"
                      placeholder="Buscar por número..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      aria-label="Buscar pedidos por número"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status-filter">Status do Pedido</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger id="status-filter" aria-label="Filtrar por status">
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      {allStatusTypes.map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Período</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-range-button"
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        aria-label="Selecionar período de datas"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                        {dateRange?.from ? (
                          dateRange.to ? (
                            <>
                              {format(dateRange.from, "dd/MM/yyyy", {
                                locale: ptBR,
                              })}{" "}
                              -{" "}
                              {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                            </>
                          ) : (
                            format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })
                          )
                        ) : (
                          <span>Selecionar período</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant={!(dateRange || statusFilter !== "all" || searchQuery) ? 'ghost' : 'info'}
                  size="sm"
                  onClick={handleResetFilters}
                  disabled={!(dateRange || statusFilter !== "all" || searchQuery)}
                  aria-label="Limpar todos os filtros de busca"
                >
                  Limpar Filtros
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        <section aria-labelledby="table-title">
          <Card>
            <CardHeader>
              <CardTitle id="table-title">Listagem de Pedidos</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {selectedClient != null ? (
                <div aria-live="polite">
                  {isLoading ? (
                    <div className="p-6">
                      <LoadingState variant="table" count={10} />
                      <span className="sr-only">Carregando seus pedidos...</span>
                    </div>
                  ) : paginatedOrders.length > 0 ? (
                    <DataTable columns={columns} data={paginatedOrders} />
                  ) : (
                    <div className="py-12 text-center" role="status">
                      <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                      <h3 className="mt-4 text-lg font-medium">
                        Nenhum pedido encontrado
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {searchQuery || statusFilter !== "all" || dateRange
                          ? "Tente ajustar os filtros de busca."
                          : "Você ainda não possui pedidos realizados."}
                      </p>
                      {(searchQuery || statusFilter !== "all" || dateRange) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={handleResetFilters}
                          aria-label="Limpar filtros para ver todos os pedidos"
                        >
                          Limpar Filtros
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center" role="alert">
                  <School className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                  <h3 className="mt-4 text-lg font-medium">
                    Selecione uma unidade escolar.
                  </h3>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {totalPages > 1 && !isLoading && (
          <nav className="mt-6 flex flex-col items-center justify-between gap-4 sm:flex-row" aria-label="Navegação de páginas">
            <div className="text-sm text-muted-foreground" aria-live="polite">
              Mostrando {(currentPage - 1) * itemsPerPage + 1} a{" "}
              {Math.min(currentPage * itemsPerPage, filteredOrders.length)} de{" "}
              {filteredOrders.length} pedidos
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(currentPage - 1)}
                    aria-label="Ir para a página anterior"
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => handlePageChange(pageNum)}
                        isActive={currentPage === pageNum}
                        className="cursor-pointer"
                        aria-label={`Ir para a página ${pageNum}`}
                        aria-current={currentPage === pageNum ? "page" : undefined}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(currentPage + 1)}
                    aria-label="Ir para a próxima página"
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </nav>
        )}
      </main>
    </div>
  );
};

export default MyOrders;
